import { useState, useEffect } from 'react';
import { Modal, Button, InputNumber, Alert, Empty } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { formatMoney } from './customerReceiptsHelpers';
import {
  buildFifoAllocation, sumAllocations, allocationRemaining, validateAllocations, allocationsToPayload
} from './receiptAllocationHelpers';

// ════════════════════════════════════════════════════════════════════
// Stage SALES-RECEIPTS-2 — allocate one on-account advance across the
// customer's confirmed proformas. Pure/props-driven: the page fetches the
// context and persists via onSave(payload); this component only does the maths
// (FIFO auto-fill, live remaining, over-allocation guard).
//   props: { open, receipt:{paymentNo,amount,customerName}, proformas:[{id,
//            proformaNo,grandTotal,currentAllocation}], saving, onCancel, onSave }
// ════════════════════════════════════════════════════════════════════
export default function AllocateAdvanceModal({ open, receipt, proformas, saving, onCancel, onSave }) {
  const [rows, setRows] = useState({});

  // Seed from the receipt's existing split each time the modal opens.
  useEffect(() => {
    if (!open) return;
    const init = {};
    (Array.isArray(proformas) ? proformas : []).forEach((p) => {
      if (p && p.id && Number(p.currentAllocation) > 0) init[p.id] = Number(p.currentAllocation);
    });
    setRows(init);
  }, [open, proformas]);

  const amount = receipt ? (Number(receipt.amount) || 0) : 0;
  const allocated = sumAllocations(rows);
  const remaining = allocationRemaining(amount, rows);
  const check = validateAllocations(amount, rows);
  const list = Array.isArray(proformas) ? proformas : [];

  const setPi = (id, val) => setRows((s) => ({ ...s, [id]: val == null ? '' : val }));
  const autoFill = () => setRows(buildFifoAllocation(amount, list));
  const clearAll = () => setRows({});
  const save = () => { if (check.ok && !saving) onSave(allocationsToPayload(rows)); };

  return (
    <Modal
      open={open}
      title="Allocate advance"
      width={560}
      onCancel={onCancel}
      destroyOnHidden
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-[12px] text-slate-500">
            Unapplied left:{' '}
            <span data-testid="alloc-remaining" className={`tabular-nums font-semibold ${remaining < 0 ? 'text-rose-600' : remaining === 0 ? 'text-emerald-600' : 'text-slate-700'}`}>
              {formatMoney(remaining)}
            </span>
          </span>
          <span className="flex gap-2">
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" loading={saving} disabled={!check.ok} onClick={save}>Save allocation</Button>
          </span>
        </div>
      }
    >
      {receipt ? (
        <div className="flex items-baseline justify-between gap-3 mb-2">
          <span className="text-[13px] font-medium text-slate-700">{receipt.paymentNo} · {receipt.customerName || '—'}</span>
          <span className="text-[14px] font-semibold text-slate-800 tabular-nums">{formatMoney(amount)}</span>
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Confirmed proformas</span>
        <span className="flex gap-2">
          <Button size="small" icon={<ThunderboltOutlined />} onClick={autoFill} disabled={!list.length}>Auto-fill (FIFO)</Button>
          <Button size="small" onClick={clearAll} disabled={allocated === 0}>Clear</Button>
        </span>
      </div>

      {list.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No confirmed proformas to allocate to." />
      ) : (
        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
          {list.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-slate-700 truncate">{p.proformaNo}</div>
                <div className="text-[11px] text-slate-400 tabular-nums">Order {formatMoney(p.grandTotal)}</div>
              </div>
              <InputNumber
                size="small" min={0} controls={false} prefix="₹" style={{ width: 140 }}
                aria-label={`alloc-${p.proformaNo}`}
                value={rows[p.id] ?? ''}
                onChange={(v) => setPi(p.id, v)}
              />
            </div>
          ))}
        </div>
      )}

      {!check.ok ? <Alert type="error" showIcon className="mt-2" message={check.error} /> : null}
      <div className="mt-2 text-[12px] text-slate-500 tabular-nums">
        Allocated <span className="font-medium text-slate-700">{formatMoney(allocated)}</span> of {formatMoney(amount)}
      </div>
    </Modal>
  );
}
